import { computed, onMounted, onServerPrefetch, ref, watch } from 'vue';

import { shiftDateWithTimezone } from '@/helpers/date-helper';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useAppStore } from '@/stores/pinia/useAppStore';

// Will be set to true after first navigation to ensure we stop using the persisted ssrTodaysDate
const didUpdateServerDate = ref(false);

/**
 * Composable to persist today's date across SSR and client side.
 *
 * - During SSR, it sets the date using the server's timezone and stores it in `ssrTodaysDate`.
 * - On the client, it updates to the correct date based on the timezone.
 * - Prevents hydration mismatches by using the precomputed SSR date before mounting.
 *
 * @returns Today's date shifted by the timezone.
 */
export function usePersistedTodaysDate() {
	const appStore = useAppStore();
	const languageStore = useLanguageStore();
	const routingStore = useRoutingStore();

	onServerPrefetch(() => {
		// On first SSR render, locale/timezone may not be loaded yet (e.g. on sports routes where
		// locales are fetched in a route guard). Fall back to UTC to avoid "Invalid time value".
		const tz = languageStore.localeObject?.timezone || 'UTC';
		appStore.setSSRTodaysDate(shiftDateWithTimezone(tz));
	});

	onMounted(() => {
		if (didUpdateServerDate.value) return;

		const unwatch = watch(
			() => routingStore.activeRoute?.path,
			(newPath, oldPath) => {
				if (oldPath != null && newPath !== oldPath) {
					didUpdateServerDate.value = true;
					unwatch();
				}
			}
		);
	});

	const todayDate = computed(() => {
		// if the user did navigate to another page we return the clients date
		if (appStore.ssrTodaysDate && !didUpdateServerDate.value) {
			return new Date(appStore.ssrTodaysDate);
		}

		const tz = languageStore.localeObject?.timezone || 'UTC';
		return shiftDateWithTimezone(tz);
	});

	return { todayDate };
}

export function usePersistedNow() {
	const appStore = useAppStore();

	onServerPrefetch(() => {
		appStore.setSSRNow(new Date());
	});

	const now = computed(() => {
		if (appStore.ssrNow && !didUpdateServerDate.value) {
			return new Date(appStore.ssrNow);
		}

		return new Date();
	});

	return { now };
}
